/**
 * cartonSplit.ts
 * Pure helpers for the "Tách Kiện" (carton-split) action.
 *
 * A phiếu is split into N identical pages that differ only by a "k/N" carton
 * number. N normally equals the number of detail rows in the section's table,
 * but the operator may instead pick an Excel column whose value says how many
 * cartons to produce (e.g. "Số kiện" = 4 → 4 cartons), independent of rows.
 */

export interface SplitGroupLike {
  headerValues: Record<string, unknown>;
  rows: Record<string, unknown>[];
}

/**
 * Resolve how many cartons a split should produce.
 * - When a split field is chosen, its value on the current record decides the
 *   count (header value first, then the first non-empty row value), as long as
 *   it reads as a number ≥ 1.
 * - Otherwise falls back to the number of detail rows.
 * Always returns ≥ 1.
 */
export function resolveCartonCount(
  group: SplitGroupLike | undefined,
  field: string,
  fallbackRows: number
): number {
  if (field) {
    let raw: unknown = group?.headerValues?.[field];
    if (raw == null || String(raw).trim() === "") {
      raw = group?.rows.find(
        (r) => r[field] != null && String(r[field]).trim() !== ""
      )?.[field];
    }
    if (raw != null) {
      const n = Number(String(raw).replace(/[^\d.]/g, ""));
      if (Number.isFinite(n) && n >= 1) return Math.floor(n);
    }
  }
  return Math.max(1, fallbackRows);
}